// ItemPicker (One Find, the price book round, DEC-019; first built for the estimate builder): find an
// item in the price book and add it. Its head names the price book and, where more than one thing is
// being built, which one it adds to (Adding to A · B · C). A search; then, before anything is typed,
// what this job usually takes (For this job), what was added lately (Recent) and the shelf of systems
// to browse; once something is typed, the kinds to look through with their counts and the rows that
// match. A row: the item's picture, its name and code, what it is, a line in ink for why it's here
// ("On 64 % of faucet jobs"), its price (and the member price under it) and a link that adds it
// ("Add to B"). After an add, what is often added with it. Nothing found says so in plain words, shows
// the nearest, and offers to clear the search or to ask the office to add it. It never takes a price:
// the price is the book's (and who may change one changes it on the line, not here).
// Reads only --picker-*; TextField, ChoiceGroup, Segmented, Link, Picture and PictureGrid read their own.
import type { ReactNode } from 'react';
import { Choice, ChoiceGroup } from '../ChoiceGroup/ChoiceGroup.tsx';
import { TextField } from '../Field/Field.tsx';
import { Link } from '../Link/Link.tsx';
import { Picture, type PictureSrc, type PictureState } from '../Picture/Picture.tsx';
import { PictureGrid, type GridTile } from '../PictureGrid/PictureGrid.tsx';
import { Segmented, type Segment } from '../Segmented/Segmented.tsx';
import './ItemPicker.css';

export interface PickerItem {
  id: string;
  name: string;
  /** The price book's code: "WH-50PV". */
  code?: string;
  /** What it is, or what comes with it. */
  detail?: string;
  price: string;
  /** For a member: "$3,460.00 for members". */
  memberPrice?: string;
  /** Its picture; with one, each row draws it. */
  picture?: PictureSrc | null;
  pictureState?: PictureState;
  /** Why it's here, in ink: "On 64 % of faucet jobs", "On this option already". */
  note?: string;
}

export interface ItemPickerProps {
  /** Its name, for its heading and screen readers ("The price book"). */
  label: string;
  query: string;
  onQueryChange: (q: string) => void;
  groups: { id: string; label: string; count?: number }[];
  group: string;
  onGroupChange: (id: string) => void;
  items: PickerItem[];
  /** What adding does, said on each row ("Add to B"). */
  addLabel: string;
  onAdd: (id: string) => void;
  /** After an add: what is often added with it. */
  suggested?: { title: string; items: PickerItem[] };
  /** The search box's words ("Find: name, code or “wh 50 gas”"). */
  placeholder?: string;
  /** What it adds to, when more than one is being built: a choice of one in its head. */
  adding?: { label: string; items: Segment[]; value: string; onChange: (id: string) => void };
  /** Before anything is typed: For this job, Recent. The kinds and the full list then wait for a search. */
  before?: { title: string; items: PickerItem[] }[];
  /** Before anything is typed, after `before`: the systems to browse into. */
  shelf?: { title: string; tiles: GridTile[]; onOpen: (id: string) => void };
  /** Words to try, under the search ("50 gal", "camera"). */
  tries?: string[];
  /** Nothing found: the nearest items, under the plain sentence. */
  nearest?: PickerItem[];
  /** Nothing found: ask the office to add it (a tech or a CSR can't add to the book). */
  onAsk?: () => void;
  /** A link under the list ("Ask the office to add it"), whatever is found. */
  foot?: ReactNode;
}

function Rows({ items, addLabel, onAdd }: Pick<ItemPickerProps, 'items' | 'addLabel' | 'onAdd'>) {
  return (
    <ul className="fl-picker__list">
      {items.map((i) => (
        <li key={i.id} className="fl-picker__item" data-pic={i.picture !== undefined ? '' : undefined}>
          {i.picture !== undefined ? (
            <span className="fl-picker__pic">
              <Picture src={i.picture} state={i.pictureState} size="md" />
            </span>
          ) : null}
          <span className="fl-picker__what">
            <span className="fl-picker__name">
              {i.name}
              {i.code ? <span className="fl-picker__code"> · {i.code}</span> : null}
            </span>
            {i.detail ? <span className="fl-picker__detail">{i.detail}</span> : null}
            {i.note ? <span className="fl-picker__note">{i.note}</span> : null}
          </span>
          <span className="fl-picker__price">
            <span>{i.price}</span>
            {i.memberPrice ? <span className="fl-picker__detail">{i.memberPrice}</span> : null}
          </span>
          <span className="fl-picker__add">
            {/* the row's name goes with the link, so a screen reader says what it adds */}
            <Link onPress={() => onAdd(i.id)}>
              {addLabel}
              <span className="fl-picker__sr">: {i.name}</span>
            </Link>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ItemPicker({
  label,
  query,
  onQueryChange,
  groups,
  group,
  onGroupChange,
  items,
  addLabel,
  onAdd,
  suggested,
  placeholder = 'Search by name or code',
  adding,
  before,
  shelf,
  tries,
  nearest,
  onAsk,
  foot,
}: ItemPickerProps) {
  const typed = query.trim() !== '';
  // with what's before you type, the kinds and the list wait for a search
  const browsing = !typed && (before?.length || shelf);
  return (
    <section className="fl-picker" aria-label={label}>
      <div className="fl-picker__head">
        <h3 className="fl-picker__title">{label}</h3>
        {adding ? (
          <Segmented
            label={adding.label}
            items={adding.items}
            selectedKey={adding.value}
            onSelectionChange={adding.onChange}
          />
        ) : null}
      </div>
      <TextField
        type="search"
        label={`Search ${label.replace(/^The /, 'the ')}`}
        hideLabel
        placeholder={placeholder}
        value={query}
        onChange={onQueryChange}
      />
      {tries?.length && !typed ? (
        <p className="fl-picker__tries">
          <span className="fl-picker__detail">Try:</span>
          {tries.map((t) => (
            <Link key={t} onPress={() => onQueryChange(t)}>
              {t}
            </Link>
          ))}
        </p>
      ) : null}
      {browsing ? null : (
        <ChoiceGroup label="Look in" selectionMode="single" value={group} onChange={onGroupChange}>
          {groups.map((g) => (
            <Choice key={g.id} value={g.id} count={g.count}>
              {g.label}
            </Choice>
          ))}
        </ChoiceGroup>
      )}
      {suggested && suggested.items.length ? (
        <div className="fl-picker__suggested">
          <p className="fl-picker__sub">{suggested.title}</p>
          <Rows items={suggested.items} addLabel={addLabel} onAdd={onAdd} />
        </div>
      ) : null}
      {browsing ? (
        <>
          {before?.map((b) =>
            b.items.length ? (
              <div key={b.title} className="fl-picker__sec">
                <p className="fl-picker__sub">{b.title}</p>
                <Rows items={b.items} addLabel={addLabel} onAdd={onAdd} />
              </div>
            ) : null,
          )}
          {shelf ? (
            <div className="fl-picker__sec">
              <p className="fl-picker__sub">{shelf.title}</p>
              <PictureGrid label={shelf.title} tiles={shelf.tiles} density="shelf" onOpen={shelf.onOpen} />
            </div>
          ) : null}
        </>
      ) : items.length ? (
        <Rows items={items} addLabel={addLabel} onAdd={onAdd} />
      ) : (
        <div className="fl-picker__none">
          {/* said plainly: a search that found nothing isn't waiting on anyone (critic K22) */}
          <p className="fl-picker__say">Nothing in the price book matches “{query.trim()}”.</p>
          {nearest?.length ? (
            <div className="fl-picker__suggested">
              <p className="fl-picker__sub">Nearest</p>
              <Rows items={nearest} addLabel={addLabel} onAdd={onAdd} />
            </div>
          ) : null}
          <p className="fl-picker__acts">
            {onAsk ? <Link onPress={onAsk}>Ask the office to add it</Link> : null}
            <Link onPress={() => onQueryChange('')}>Clear the search</Link>
          </p>
        </div>
      )}
      {foot ? <div className="fl-picker__foot">{foot}</div> : null}
    </section>
  );
}
